import { ExcelButton } from "@/controls/buttons/Excel-button";
import { SlagReportControl } from "@/controls/slag-report-control/SlagReportControl";
import {
	SimpleSelectorControl,
	getMomentTz,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import {
	Box,
	Checkbox,
	FormControlLabel,
	FormGroup,
	Grid2 as Grid,
	Paper,
	Theme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DatePicker } from "@mui/x-date-pickers";
import { Moment } from "moment-timezone";
import { DynamicGenericReportFilter } from "../../models/GenericReportFilter";
import {
	GenericReportFilterDataSource,
	GenericReportMetadataFilter,
} from "../../models/GenericReportMetadataFilter";
import { useGenericReportContext } from "../GenericReportContext";

const useStyles = makeStyles((theme: Theme) => ({
	selectorAlignment: {
		"& .MuiOutlinedInput-root": {
			height: 50,
			display: "flex",
			alignItems: "center",
		},
		"& .MuiSelect-select": {
			display: "flex",
			alignItems: "center",
			paddingTop: "0 !important",
			paddingBottom: "0 !important",
		},
	},
}));

type Props = {
	metadataFilters: GenericReportMetadataFilter[];
	filters: DynamicGenericReportFilter;
	filterSources: GenericReportFilterDataSource[][];
	updateFilters: (newValue: DynamicGenericReportFilter) => void;
	refreshData: () => void;
	onExcelExport: () => void;
};

const moment = getMomentTz();

export const ReportFilterPanel = ({
	metadataFilters,
	filters,
	filterSources,
	updateFilters,
	onExcelExport,
}: Props) => {
	const classes = useStyles();
	const {
		state: { settings },
	} = useUTLSettingsContext();
	const { state } = useGenericReportContext();
	const isSlagReport = state.reportCode === "mes-lab-slag-samples";
	const filteredMetadata = metadataFilters.filter(
		(m) => m.Parameter !== "IncludeExternal",
	);
	const gridColumns = filteredMetadata.length === 1 ? 12 : 6;
	const filtersSpace = filteredMetadata.length === 1 ? 2 : 3;
	const isDateInvalid = moment(filters.startDate).isAfter(filters.endDate);

	const getDataSource = (index: number): any[] => {
		const result: any[] = [];
		filterSources[index]?.forEach((item) => {
			result.push({
				id: item.Id,
				description: item.Name,
			});
		});
		return result;
	};

	const onChangeFilter = (value: any, key: string) => {
		const newValues: DynamicGenericReportFilter = {
			...filters,
		};
		if (key === "startDate" || key === "endDate") newValues[key] = value;
		else newValues[key] = value;
		updateFilters(newValues);
	};

	return (
		<Grid container spacing={1} style={{ margin: "10px 0px 0px 0px" }}>
			<Grid size={{ xs: 12 }} style={{ marginBottom: "10px" }}>
				<Grid
					container
					direction="row"
					justifyContent="flex-start"
					alignItems="stretch"
					spacing={1}
				>
					{filters.showStartDate && filters.showEndDate && (
						<Grid size={{ xs: "auto" }} style={{ display: "flex" }}>
							<Paper
								elevation={1}
								style={{
									display: "flex",
									alignItems: "center",
									padding: "10px",
								}}
							>
								<Grid container spacing={2}>
									<Grid size={{ xs: 6 }}>
										<DatePicker
											label="Start"
											format="MM/DD/YYYY"
											value={moment(filters.startDate) as any}
											onChange={(value: Moment | null) => {
												if (value) onChangeFilter(value.toDate(), "startDate");
											}}
											disableFuture
											maxDate={moment(filters.endDate) as any}
											slotProps={{
												textField: {
													variant: "outlined",
													size: "small",
													inputProps: { style: { width: "110px" } },
													error: isDateInvalid,
													helperText: isDateInvalid
														? "Start must be before end"
														: "",
												},
											}}
										/>
									</Grid>
									<Grid size={{ xs: 6 }}>
										<DatePicker
											label="End"
											format="MM/DD/YYYY"
											value={moment(filters.endDate) as any}
											onChange={(value: Moment | null) => {
												if (value) onChangeFilter(value.toDate(), "endDate");
											}}
											disableFuture
											minDate={moment(filters.startDate) as any}
											slotProps={{
												textField: {
													variant: "outlined",
													size: "small",
													inputProps: { style: { width: "110px" } },
												},
											}}
										/>
									</Grid>
								</Grid>
							</Paper>
						</Grid>
					)}

					{isSlagReport ? (
						<Grid size={{ xs: "auto" }} style={{ display: "flex" }}>
							<SlagReportControl settings={settings} />
						</Grid>
					) : (
						!isSlagReport &&
						metadataFilters.filter((m) => m.Parameter !== "IncludeExternal")
							.length > 0 && (
							<Grid
								size={{ xs: "auto", md: filtersSpace }}
								style={{ display: "flex" }}
							>
								<Paper
									elevation={1}
									style={{
										padding: "8px",
										display: "flex",
										alignItems: "center",
									}}
									className={classes.selectorAlignment}
								>
									<Grid container spacing={2} alignItems="center">
										{filteredMetadata.map((item, index) => (
											<Grid
												size={{ xs: 12, md: gridColumns }}
												key={`grid_${index}`}
												sx={{ minWidth: 180 }}
											>
												<SimpleSelectorControl
													title={item.FilterName}
													dataSource={getDataSource(index)}
													onChange={(value: string) =>
														onChangeFilter(value, item.Parameter)
													}
													selectedValue={filters[item.Parameter]}
													styleList={{ width: "100%" }}
												/>
											</Grid>
										))}
									</Grid>
								</Paper>
							</Grid>
						)
					)}

					{metadataFilters.some((m) => m.Parameter === "IncludeExternal") && (
						<Grid size={{ xs: "auto" }} style={{ display: "flex" }}>
							<Paper
								elevation={1}
								style={{
									display: "flex",
									alignItems: "center",
								}}
							>
								<Box sx={{ p: 2 }}>
									<FormGroup>
										<FormControlLabel
											control={
												<Checkbox
													checked={!!filters.IncludeExternal}
													color="primary"
													onChange={(event) => {
														onChangeFilter(
															event.target.checked,
															"IncludeExternal",
														);
													}}
												/>
											}
											label="Include external heats"
										/>
									</FormGroup>
								</Box>
							</Paper>
						</Grid>
					)}

					<Grid
						size={{ xs: "auto" }}
						style={{
							marginLeft: "auto",
							display: "flex",
							alignItems: "center",
						}}
					>
						<ExcelButton onClick={onExcelExport} />
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
